import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useLocation as useGeo } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, MapPin, Star, IndianRupee, Bed, FlaskConical, Pill, Ambulance,
  Stethoscope, Hospital as HospitalIcon, Phone, Video, Clock, ShieldCheck, Navigation, ImagePlus,
} from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const facilityMarkerIcon = (color) => L.divIcon({
  html: `<div style="background:${color};color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.25);font-weight:700;font-size:12px">📍</div>`,
  className: "", iconSize: [32, 32], iconAnchor: [16, 16],
});

const KIND_META = {
  doctor: { icon: Stethoscope, label: "Doctor / Clinic", color: "bg-teal-500" },
  hospital: { icon: HospitalIcon, label: "Hospital", color: "bg-cyan-600" },
  lab: { icon: FlaskConical, label: "Diagnostic Lab", color: "bg-emerald-500" },
  pharmacy: { icon: Pill, label: "Pharmacy", color: "bg-amber-500" },
  ambulance: { icon: Ambulance, label: "Ambulance", color: "bg-red-600" },
};

const CTA = {
  doctor: { to: "/book-opd", label: "Book OPD with this doctor" },
  hospital: { to: "/emergency", label: "Reserve a bed here" },
  lab: { to: "/lab-tests", label: "Book tests here" },
  pharmacy: { to: "/pharmacy", label: "Order medicines" },
  ambulance: { to: "/emergency", label: "Book an ambulance" },
};

export default function FacilityDetail() {
  const { kind, id } = useParams();
  const nav = useNavigate();
  const { loc } = useGeo();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const canUpload = user && ["admin", "field_executive", "partner"].includes(user.role);

  const load = () => {
    api.get(`/facilities/${kind}/${id}`)
      .then((r) => setItem(r.data.data))
      .catch(() => setErr("Facility not found"));
  };
  useEffect(() => { load(); }, [kind, id]);

  const uploadMedia = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post("/facilities/media", {
        kind, facility_id: id,
        media_type: file.type.startsWith("video") ? "video" : "image",
        file_base64: base64, caption,
      });
      toast.success("Media added to gallery");
      setCaption("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  if (err) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <div className="text-slate-500 mb-4">{err}</div>
      <Button variant="outline" onClick={() => nav("/search")} data-testid="back-to-search"><ArrowLeft className="w-4 h-4 mr-1" /> Back to search</Button>
    </div>
  );
  if (!item) return <div className="p-10 text-center">Loading…</div>;

  const meta = KIND_META[kind] || KIND_META.hospital;
  const cta = CTA[kind];
  const beds = item.beds || {};

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="detail-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>

      <Card className="mt-4 p-6 sm:p-8 rounded-3xl" data-testid="facility-detail-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {item.image ? (
              <img src={item.image} alt={item.name} data-testid="facility-photo"
                className="w-16 h-16 rounded-2xl object-cover object-top border border-slate-200" />
            ) : (
              <div className={`w-14 h-14 rounded-2xl ${meta.color} text-white flex items-center justify-center`}>
                <meta.icon className="w-7 h-7" />
              </div>
            )}
            <div>
              <Badge variant="outline" className="mb-1">{meta.label}</Badge>
              <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="facility-name">{item.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
                {item.address && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{item.address}</span>}
                {item.city && <span>{item.city}</span>}
                {item.rating && (
                  <span className="inline-flex items-center gap-1 font-semibold text-slate-800">
                    <Star className="w-4 h-4 fill-amber-500 text-amber-500" />{item.rating}
                  </span>
                )}
                {item.phone && (
                  <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 text-teal-700 font-semibold" data-testid="facility-phone">
                    <Phone className="w-4 h-4" />{item.phone}
                  </a>
                )}
              </div>
            </div>
          </div>
          <Link to={cta.to}>
            <Button className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="facility-cta">{cta.label}</Button>
          </Link>
        </div>

        <div className="my-6 border-t border-slate-200" />

        {/* Doctor specifics */}
        {kind === "doctor" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DetailStat label="Specialization" value={item.specialization} />
            <DetailStat label="Experience" value={`${item.experience} years`} />
            <DetailStat label="Consultation fee" value={<span className="inline-flex items-center"><IndianRupee className="w-4 h-4" />{item.fee}</span>} />
            <DetailStat label="Modes" value={(item.modes || []).map((m) => m.replace("_", " ")).join(" + ")} />
          </div>
        )}

        {/* Hospital bed availability */}
        {kind === "hospital" && (
          <div className="space-y-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3 inline-flex items-center gap-1">
                <Bed className="w-3 h-3" /> Live bed availability
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["general", "icu", "nicu", "ventilator"].map((k) => (
                  <Card key={k} className="p-4 text-center" data-testid={`bed-${k}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                    <div className="font-mono text-2xl font-bold">{beds[k] ?? 0}</div>
                    <Badge className={`mt-1 text-[10px] ${(beds[k] ?? 0) > 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"} border-0`}>
                      {(beds[k] ?? 0) > 0 ? "Available" : "Full"}
                    </Badge>
                  </Card>
                ))}
              </div>
            </div>

            {item.empanelments?.length > 0 && (
              <Section title="Empanelment (Cashless)" items={item.empanelments} testid="hosp-empanelments" tone="teal" />
            )}
            {item.in_house_facilities?.length > 0 && (
              <Section title="In-house facilities" items={item.in_house_facilities} testid="hosp-facilities" tone="cyan" />
            )}
            {item.specialities?.length > 0 && (
              <Section title="Speciality type" items={item.specialities} testid="hosp-specialities" tone="emerald" />
            )}
            {item.departments?.length > 0 && (
              <Section title="Departments" items={item.departments} testid="hosp-departments" tone="slate" />
            )}

            {item.hospital_doctors?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Doctors at this hospital</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {item.hospital_doctors.map((d, i) => (
                    <Card key={i} className="p-4" data-testid={`hosp-doc-${i}`}>
                      <div className="font-semibold">{d.name}</div>
                      <div className="text-xs text-slate-600">{d.specialization}</div>
                      <div className="text-xs text-slate-500 mt-1">{d.degree} · {d.experience}y exp</div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {item.registration && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Hospital registration</div>
                <Card className="p-4 grid sm:grid-cols-2 gap-3 text-sm" data-testid="hosp-registration">
                  {item.registration.registration_no && <RegRow label="Registration no." value={item.registration.registration_no} />}
                  {item.registration.license_no && <RegRow label="License no." value={item.registration.license_no} />}
                  {item.registration.established_year && <RegRow label="Established" value={item.registration.established_year} />}
                  {item.registration.accreditation && <RegRow label="Accreditation" value={item.registration.accreditation} />}
                  {item.registration.governing_body && <RegRow label="Governing body" value={item.registration.governing_body} />}
                </Card>
              </div>
            )}

            {item.bed_charges && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Bed charges (per day)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bed-charges">
                  {["general","icu","nicu","ventilator"].map((k) => (
                    <Card key={k} className="p-4 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                      <div className="font-mono text-xl font-bold text-slate-900 mt-1">₹{item.bed_charges[k] ?? "—"}</div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lab tests catalogue */}
        {kind === "lab" && (
          <div className="space-y-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Test catalogue & pricing</div>
              {item.test_catalogue?.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-2" data-testid="lab-catalogue">
                  {item.test_catalogue.map((t, i) => (
                    <Card key={i} className="p-3 flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-sm">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.category || "General"}</div>
                      </div>
                      <div className="font-mono font-bold text-emerald-700">₹{t.price}</div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(item.tests || []).map((t) => <Badge key={t} variant="outline" className="text-xs py-1 px-3">{t}</Badge>)}
                </div>
              )}
            </div>
            {item.equipment?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-2">Equipment on-site</div>
                <div className="flex flex-wrap gap-2">
                  {item.equipment.map((e, i) => <Badge key={i} variant="outline" className="text-xs py-1 px-3">{e}</Badge>)}
                </div>
              </div>
            )}
            {item.home_collection && (
              <div className="inline-flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                <Clock className="w-4 h-4" /> Home sample collection available
              </div>
            )}
          </div>
        )}

        {/* Pharmacy */}
        {kind === "pharmacy" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <DetailStat label="Delivery time" value={`~${item.delivery_time_min} minutes`} />
            <DetailStat label="Home delivery" value="Available" />
          </div>
        )}

        {/* Ambulance */}
        {kind === "ambulance" && (
          <div className="grid sm:grid-cols-3 gap-4">
            <DetailStat label="Type" value={(item.type || "").toUpperCase()} />
            <DetailStat label="Vehicle" value={item.vehicle_number} />
            <DetailStat label="Status" value={item.status} />
            <DetailStat label="Driver" value={item.driver_name} />
            <DetailStat label="Driver phone" value={item.driver_phone} />
            <DetailStat label="Base fare" value={item.type === "icu" ? "₹4,500" : item.type === "als" ? "₹2,500" : "₹800"} />
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-teal-600" /> Verified MedHOne network partner
        </div>
      </Card>

      {/* Media gallery */}
      {(item.media?.length > 0 || canUpload) && (
        <Card className="mt-6 p-5 rounded-2xl" data-testid="facility-gallery">
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Gallery</div>
          {item.media?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {item.media.map((m) => (
                m.type === "video" ? (
                  <video key={m.id} src={m.url} controls className="w-full h-40 object-cover rounded-lg border border-slate-200" />
                ) : (
                  <img key={m.id} src={m.url} alt={m.caption || item.name} className="w-full h-40 object-cover rounded-lg border border-slate-200" />
                )
              ))}
            </div>
          )}
          {canUpload && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2 inline-flex items-center gap-1">
                <ImagePlus className="w-3 h-3" /> Add photo / video
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[180px]">
                  <Label>Caption (optional)</Label>
                  <Input data-testid="media-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Reception, ICU ward…" />
                </div>
                <div>
                  <Input data-testid="media-file" type="file" accept="image/*,video/*" disabled={uploading}
                    onChange={(e) => uploadMedia(e.target.files?.[0])} className="max-w-[240px]" />
                </div>
              </div>
              {uploading && <div className="text-xs text-teal-700 mt-2">Uploading…</div>}
            </div>
          )}
        </Card>
      )}

      {item.lat && item.lng && (
        <Card className="mt-6 p-0 rounded-3xl overflow-hidden" data-testid="facility-map-card">
          <div className="p-4 sm:p-5 flex items-center justify-between flex-wrap gap-2 border-b">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 inline-flex items-center gap-2">
              <MapPin className="w-3 h-3" /> Location & directions
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                data-testid="directions-google"
                target="_blank" rel="noreferrer"
                href={
                  loc
                    ? `https://www.google.com/maps/dir/?api=1&origin=${loc.lat},${loc.lng}&destination=${item.lat},${item.lng}&travelmode=driving`
                    : `https://www.google.com/maps/dir/?api=1&destination=${item.lat},${item.lng}&travelmode=driving`
                }
              >
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Navigation className="w-4 h-4 mr-2" /> Google Maps
                </Button>
              </a>
              <a
                data-testid="directions-osm"
                target="_blank" rel="noreferrer"
                href={
                  loc
                    ? `https://www.openstreetmap.org/directions?from=${loc.lat}%2C${loc.lng}&to=${item.lat}%2C${item.lng}`
                    : `https://www.openstreetmap.org/?mlat=${item.lat}&mlon=${item.lng}#map=16/${item.lat}/${item.lng}`
                }
              >
                <Button size="sm" variant="outline">OpenStreetMap</Button>
              </a>
            </div>
          </div>
          <div style={{ height: 380 }}>
            <MapContainer center={[item.lat, item.lng]} zoom={14} style={{ height: "100%", width: "100%" }} data-testid="facility-map">
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
              />
              <Marker position={[item.lat, item.lng]} icon={facilityMarkerIcon("#0F766E")}>
                <Popup>{item.name}<br />{item.address || ""}</Popup>
              </Marker>
              {loc && (
                <Marker position={[loc.lat, loc.lng]} icon={facilityMarkerIcon("#1D5BD8")}>
                  <Popup>Your location</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </Card>
      )}
    </div>
  );
}

function DetailStat({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold mt-1 capitalize">{value}</div>
    </Card>
  );
}

const TONES = {
  teal: "bg-teal-50 text-teal-800 border-teal-200",
  cyan: "bg-cyan-50 text-cyan-800 border-cyan-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
};
function Section({ title, items, testid, tone = "slate" }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-2">{title}</div>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {items.map((t, i) => (
          <span key={i} className={`text-xs px-3 py-1 rounded-full border ${TONES[tone]}`}>{t}</span>
        ))}
      </div>
    </div>
  );
}
function RegRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold text-sm mt-0.5">{value}</div>
    </div>
  );
}
